/**
 * Turns browser autofill off on every field of the page.
 *
 * Chrome ignores the "off" value on address, name and phone fields; "new-password" is the value
 * that really stops its suggestions, and on a non-password field it triggers no save prompt.
 * Login fields declare "username" / "current-password" and are left alone, so password managers
 * keep working. PrimeVue re-sets "off" on some inputs at each render: attribute changes are
 * watched too, and the guard only writes when the value differs, which rules out a loop.
 */

const PRESERVED = new Set(["username", "current-password"]);
const FORCED = "new-password";

function guardField(element: Element): void {
  if (element.tagName !== "INPUT" && element.tagName !== "TEXTAREA") {
    return;
  }
  const current = element.getAttribute("autocomplete");
  if (current !== null && PRESERVED.has(current)) {
    return;
  }
  if (current !== FORCED) {
    element.setAttribute("autocomplete", FORCED);
  }
}

function guardTree(root: Element): void {
  guardField(root);
  root.querySelectorAll("input, textarea").forEach(guardField);
}

/** Applies the rule to what exists now and to every field added or changed later. Returns the observer. */
export function installAutocompleteGuard(root: HTMLElement): MutationObserver {
  guardTree(root);
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes") {
        guardField(mutation.target as Element);
        continue;
      }
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          guardTree(node as Element);
        }
      });
    }
  });
  observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["autocomplete"] });
  return observer;
}
